import type { ReactNode } from 'react'

export interface Stat {
  label: string
  value: ReactNode
  /** Overrides the default foreground, e.g. pink for open issues. */
  color?: string
}

/**
 * The 4-up figure strip under the hero trend. Hairline-divided cells, mono
 * instrument labels, display-face numerals.
 *
 * Collapses 4 -> 2 columns below `sm` so the numerals never squeeze on a
 * phone; the right/bottom borders are driven by index so no cell carries a
 * trailing rule at the grid edge.
 */
export function StatStrip({ stats }: { stats: Stat[] }) {
  return (
    <div className='grid grid-cols-2 border-t border-hairline sm:grid-cols-4'>
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={[
            'flex flex-col gap-1 px-5 py-3.5',
            // 2-col: rule after the first cell of each row.
            i % 2 === 0 ? 'border-r border-hairline' : '',
            // 2-col: rule under the top row.
            i < stats.length - 2 ? 'border-b border-hairline' : '',
            // 4-col: every cell but the last gets a right rule, nothing gets
            // a bottom rule.
            'sm:border-b-0',
            i === stats.length - 1 ? 'sm:border-r-0' : 'sm:border-r',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className='instrument-label text-instrument leading-none'>
            {s.label}
          </span>
          <span
            className='font-display text-xl font-semibold leading-none'
            style={s.color ? { color: s.color } : undefined}
          >
            {s.value}
          </span>
        </div>
      ))}
    </div>
  )
}
